import { useMemo, useState } from "react";
import { Compass } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useJourneyInstancesList } from "./use-journey-instances-list";
import { JourneyCard } from "./journey-card";
import type { PortalJourneyListRow } from "./types";

// /portal/journeys — partner-facing list of journey assignments.
//
// Sub-bucket 4 promoted legacy's flat 2-column grid into an active/completed
// segmented strip per the brief. Segmentation is client-side; the server
// returns every assignment in one call (small data).
//
// "Active" includes any assignment without a `completed_at` AND with at least
// one open task (or zero tasks — newly-assigned journeys). "Completed" is
// the inverse — either the assignment has an explicit completed_at or
// every task is done.

type Segment = "active" | "completed";

export function JourneysListPage() {
  const query = useJourneyInstancesList();
  const [segment, setSegment] = useState<Segment>("active");

  const { active, completed } = useMemo(() => {
    const all = query.data ?? [];
    const a: PortalJourneyListRow[] = [];
    const c: PortalJourneyListRow[] = [];
    for (const row of all) {
      const allDone =
        row.total_public_tasks > 0 &&
        row.completed_public_tasks >= row.total_public_tasks;
      if (row.completed_at !== null || allDone) {
        c.push(row);
      } else {
        a.push(row);
      }
    }
    return { active: a, completed: c };
  }, [query.data]);

  const visible = segment === "active" ? active : completed;

  return (
    <div>
      <div className="mb-7">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
          Journeys
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Multi-step programs your Bookboost team is running with you — from
          onboarding to ongoing enablement.
        </p>
      </div>

      <Tabs
        value={segment}
        onValueChange={(v) => setSegment(v as Segment)}
        className="mb-5"
      >
        <TabsList>
          <TabsTrigger value="active">
            Active{active.length ? ` · ${active.length}` : ""}
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed{completed.length ? ` · ${completed.length}` : ""}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {query.isLoading ? (
        <ul className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i}>
              <Skeleton className="h-32 w-full rounded-xl" />
            </li>
          ))}
        </ul>
      ) : query.isError ? (
        <div className="rounded-xl border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Couldn’t load your journeys. Refresh to try again.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <EmptySegment segment={segment} />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {visible.map((row) => (
            <li key={row.id}>
              <JourneyCard row={row} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptySegment({ segment }: { segment: Segment }) {
  return (
    <div className="rounded-xl border border-dashed bg-card p-10 text-center">
      <Compass className="mx-auto h-6 w-6 text-muted-foreground" />
      <h3 className="mt-3 font-display text-base font-semibold tracking-tight">
        {segment === "active" ? "No active journeys" : "No completed journeys yet"}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {segment === "active"
          ? "Your partner manager hasn’t assigned any journeys you’re working on right now."
          : "Once you finish a journey it’ll appear here."}
      </p>
    </div>
  );
}
